import { Injectable } from '@angular/core'
import { FormworkId } from '@efp/api'
import { createStore } from '@ngneat/elf'
import {
  addEntities,
  deleteEntities,
  getAllEntitiesApply,
  getEntity,
  selectAllEntities,
  setEntities,
  updateEntities,
  upsertEntities,
  withEntities,
} from '@ngneat/elf-entities'
import { FavouriteProfile } from '../models/favourites'
import { FavouriteDao } from '../services/dao/favourite.dao'
import { Doka360ProjectSelectorService } from '../services/doka360-project-selector.service'

export const favouriteStore = createStore({ name: 'favourites' }, withEntities<FavouriteProfile>())

@Injectable({
  providedIn: 'root',
})
export class FavouriteRepository {
  private hasLoadedAllForScope = false
  public readonly favourites$ = favouriteStore.pipe(selectAllEntities())

  constructor(
    private readonly favouriteDao: FavouriteDao,
    private readonly doka360ProjectSelectorService: Doka360ProjectSelectorService
  ) {
    this.doka360ProjectSelectorService.currentDoka360ProjectUid$.subscribe(() => {
      this.hasLoadedAllForScope = false
    })
  }

  public async create(
    favourite: Omit<FavouriteProfile, 'id' | 'createdAt' | 'updatedAt'>
  ): Promise<number> {
    favourite.doka360ProjectUid = this.doka360ProjectSelectorService.getCurrentDoka360ProjectUid()

    const id = await this.favouriteDao.create(favourite)
    const newFavourite = {
      ...favourite,
      id,
      createdAt: new Date(),
      updatedAt: new Date(),
    }

    favouriteStore.update(addEntities(newFavourite))
    return newFavourite.id
  }

  public async update(favourite: FavouriteProfile): Promise<void> {
    await this.favouriteDao.update(favourite)
    favouriteStore.update(updateEntities(favourite.id, favourite))
  }

  public async delete(id: number): Promise<void> {
    await this.favouriteDao.deleteById(id)
    favouriteStore.update(deleteEntities(id))
  }

  public async fetchAllByScopeSelector(): Promise<void> {
    const currentDoka360ProjectUid =
      this.doka360ProjectSelectorService.getCurrentDoka360ProjectUid()

    /* Use cache only for standalone user scope,
       because cache invalidation is not automatically trigger by other user/client changes
    */
    if (!currentDoka360ProjectUid && this.hasLoadedAllForScope) {
      const cachedFavorites = favouriteStore.query(
        getAllEntitiesApply({
          filterEntity: (e) => e.doka360ProjectUid === undefined,
        })
      )

      if (cachedFavorites && cachedFavorites.length > 0) {
        return
      }
    }

    const favourites = await this.favouriteDao.findAll(currentDoka360ProjectUid)
    favouriteStore.update(setEntities(favourites))
    this.hasLoadedAllForScope = true
  }

  public async findAllByFormworkSystemId(
    formworkSystemId: FormworkId
  ): Promise<FavouriteProfile[]> {
    const currentDoka360ProjectUid =
      this.doka360ProjectSelectorService.getCurrentDoka360ProjectUid()

    const cachedFavorites = favouriteStore.query(
      getAllEntitiesApply({
        filterEntity: (e) =>
          e.formworkSystemId === formworkSystemId &&
          e.doka360ProjectUid === currentDoka360ProjectUid,
      })
    )

    if (this.hasLoadedAllForScope && cachedFavorites && cachedFavorites.length > 0) {
      return cachedFavorites
    }

    const favourites = await this.favouriteDao.findAllByFormworkSystemId(
      formworkSystemId,
      currentDoka360ProjectUid
    )
    favouriteStore.update(upsertEntities(favourites))
    return favourites
  }

  public async findOneById(id: number): Promise<FavouriteProfile | undefined> {
    let favourite = favouriteStore.query(getEntity(id))

    if (!favourite) {
      favourite = await this.favouriteDao.findOneById(id)

      if (favourite) {
        favouriteStore.update(upsertEntities(favourite))
      }
    }

    return favourite
  }

  public async findOneStandardByFormworkSystemId(
    formworkSystemId: FormworkId
  ): Promise<FavouriteProfile | undefined> {
    const currentDoka360ProjectUid =
      this.doka360ProjectSelectorService.getCurrentDoka360ProjectUid()

    const favourites = favouriteStore.query(
      getAllEntitiesApply({
        filterEntity: (e) =>
          e.formworkSystemId === formworkSystemId &&
          e.isStandard &&
          e.doka360ProjectUid === currentDoka360ProjectUid,
      })
    )

    let favourite = favourites && favourites.length > 0 ? favourites[0] : undefined

    if (!favourite) {
      favourite = await this.favouriteDao.findOneStandardByFormworkSystemId(
        formworkSystemId,
        currentDoka360ProjectUid
      )

      if (favourite) {
        favouriteStore.update(upsertEntities(favourite))
      }
    }

    return favourite
  }
}
